import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@ps-design-system/components/Form';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Payments from '../../../../../api/api-payments';
import {
  createIdentitySession,
  fetchIdentitySession
} from '../../services/identity-verification-service';
import { useStripeInstanceContext } from '../../StripeInstanceContextProvider';
import { IDVErrors } from './identity-verification-utils';
import { IdentityVerificationButton } from './IdentityVerificationButton';

export const IdentityVerificationField = ({
  alcoholConsumed,
  schedule,
  listing,
  SSOid,
  accessToken,
  setFormValue,
  setFormError,
  name = 'identityVerificationStatus',
  control,
  intl
}) => {
  const { stripeInstance } = useStripeInstanceContext();
  const [retry, setRetry] = React.useState(false);

  const [verificationSuccess, setVerificationSuccess] = React.useState(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [, setPollingTicks] = React.useState(0);
  const [isPollingStatus, setIsPollingStatus] = React.useState(false);
  const [pollingInterval, setPollingInterval] = React.useState(null);

  const handleErrorChange = (errorMsgObj) => {
    setFormError?.('identityVerificationStatus', {
      type: 'manual',
      message: !errorMsgObj
        ? ''
        : intl.formatMessage(errorMsgObj, {
            link: (
              <a
                target="_blank"
                style={{ textDecoration: 'underline' }}
                href={`https://support.peerspace.com/hc/${intl?.locale?.toLowerCase()}/requests/new`}
                rel="noopener noreferrer"
              >
                <FormattedMessage
                  id="booking_request.identity_verification.here"
                  defaultMessage="here"
                />
              </a>
            )
          })
    });
  };

  const handleVerificationSuccess = () => {
    analyticsIntegrations.statusUpdateIdentityVerification({
      status: 'verified',
      listing_id: listing?._id,
      alcohol_consumed: alcoholConsumed,
      schedule
    });

    setVerificationSuccess(true);
    setFormValue(name, 'verified');
    setIsPollingStatus(false);
    setIsLoading(false);
    setPollingTicks(0);
  };

  const handleVerificationFailure = (failureCode, retry) => {
    analyticsIntegrations.statusUpdateIdentityVerification({
      status: 'failed',
      listing_id: listing?._id,
      alcohol_consumed: alcoholConsumed,
      schedule
    });

    setVerificationSuccess(false);
    setFormValue(name, 'failed');
    setIsPollingStatus(false);
    setIsLoading(false);
    setPollingTicks(0);
    setRetry(retry);
    handleErrorChange(IDVErrors[failureCode] || IDVErrors.generic_error);
  };

  const handlePollingTimeout = (failureCode) => {
    setVerificationSuccess(false);
    setIsPollingStatus(false);
    setIsLoading(false);
    setPollingTicks(0);
    handleErrorChange(IDVErrors[failureCode] || IDVErrors.generic_error);
  };

  const scheduleStatusPoll = () => {
    setPollingInterval(
      setTimeout(() => {
        // eslint-disable-next-line no-use-before-define
        pollIdentityStatus();
      }, 2000)
    );
  };

  const pollIdentityStatus = async () => {
    try {
      const session = await fetchIdentitySession(SSOid, accessToken);
      const { verification_status, failure_code, retry } = session || {};

      if (verification_status === 'verified') {
        handleVerificationSuccess();
      } else if (verification_status === 'failed') {
        handleVerificationFailure(failure_code, retry);
      } else {
        setPollingTicks((prevTick) => {
          const nextTick = prevTick + 1;
          if (nextTick >= 15) {
            // 30 seconds
            handlePollingTimeout(failure_code);
          } else {
            scheduleStatusPoll();
          }
          return nextTick;
        });
      }
    } catch (error) {
      handleErrorChange(IDVErrors.generic_error);
    }
  };

  const initiateSession = async () => {
    setIsLoading(true);
    handleErrorChange(null);

    try {
      const sessionPayload = {
        trigger_type: 'listing',
        trigger_id: listing?._id,
        alcohol: alcoholConsumed,
        metro: listing?.metro,
        country: listing?.country,
        schedule: schedule
      };
      const session = await createIdentitySession(
        SSOid,
        accessToken,
        sessionPayload
      );

      stripeInstance
        .verifyIdentity(session?.client_secret)
        .then((result) => {
          if (result?.error) {
            handleErrorChange(
              IDVErrors[result.error] || IDVErrors.generic_error
            );
            setIsPollingStatus(false);
            setIsLoading(false);
          } else {
            setIsPollingStatus(true);
          }
        })
        .catch(() => {
          setFormError?.(
            'identityVerificationStatus',
            intl.formatMessage(IDVErrors.generic_error)
          );
          setIsPollingStatus(false);
        });
    } catch (error) {
      handleErrorChange(IDVErrors.generic_error);
      setIsPollingStatus(false);
    }
  };

  React.useEffect(() => {
    if (!isPollingStatus) {
      return;
    }

    scheduleStatusPoll();
    return () => {
      clearTimeout(pollingInterval);
    };
  }, [isPollingStatus]);

  React.useEffect(() => {
    Payments.getIdentitySession(SSOid, accessToken, (existingSession) => {
      if (existingSession && existingSession.session_id) {
        setRetry(existingSession?.retry);
        if (!existingSession?.retry) {
          // If user cannot retry, then they have previously failed due to a non-retry failure.
          handleErrorChange(IDVErrors.retry_not_allowed);
        }
      }
    });
  }, []);

  if (verificationSuccess) {
    return (
      <div
        data-testingid="identity-verification-success"
        className="flex middle h6 no-top-margin text-gray-dark"
      >
        <img
          className="icon margin-md-right"
          src="https://assets.peerspace.com/blue-verified.svg?v=rb"
          alt="Verification Icon"
        />
        <span>
          <FormattedMessage
            id="identity_verification.verified"
            defaultMessage="Verified"
          />
        </span>
      </div>
    );
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <IdentityVerificationButton
              ref={field.ref}
              fieldValue={field.value}
              retry={retry}
              onClick={initiateSession}
              stripeInstance={stripeInstance}
              isLoading={isLoading}
              locale={intl?.locale}
            />
          </FormControl>

          <FormMessage />
        </FormItem>
      )}
    ></FormField>
  );
};

IdentityVerificationField.displayName = 'IdentityVerificationField';
IdentityVerificationField.propTypes = {
  alcoholConsumed: PropTypes.bool,
  schedule: PropTypes.string,
  listing: PropTypes.object,
  SSOid: PropTypes.string,
  accessToken: PropTypes.string,
  setFormValue: PropTypes.func,
  setFormError: PropTypes.func,
  control: PropTypes.object,
  name: PropTypes.string,
  intl: PropTypes.object
};
